import {
  AccountBalanceOutlined,
  ApartmentOutlined,
  ArrowBack,
  ArrowForward,
  CheckCircleOutlined,
  InfoOutlined,
  KeyboardDoubleArrowRightOutlined,
  LocalAtmOutlined,
  OpenInNew,
  ShowChartOutlined,
  SyncAltOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import IconButtonSquare from 'further-ui/components/IconButton';
import {
  AccountingPeriodStatus,
  AccountingPeriodChecklist,
  ChecklistItem,
  PeriodChecklistCounts,
} from 'further-types/lp/accounting-period';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import ChecklistCard from 'components/new/ChecklistCard';
import ProcessingPage from 'components/new/ProcessingPage';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'components/Layout';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { fromPairs } from 'lodash';
import api from 'lib/trpcClient';
import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  createApprovePrepayments,
  createApproveTransactionRatesRoute,
  createLpPostUnrealisedFXGainsRoute,
  createReviewAccrualsRoute,
  LP_PERIOD_END_REVIEW,
} from 'adminConstants/routes';
import { captureException } from '@sentry/react';

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(theme.spacingSizes.sm),
  paddingBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const BottomBar = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.common.white,
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  marginTop: theme.spacing(theme.spacingSizes.sm),
}));

const ChecklistItemIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  fontSize: 20,
}));

type RouteParams = {
  periodId: string;
};

type ChecklistItemDef = {
  key: keyof AccountingPeriodChecklist;
  label: string;
  subLabelTemplate: (count: string) => string;
  icon: React.ReactNode;
  formatValue?: (value: number) => string;
};

const formatCount = (
  counts: PeriodChecklistCounts | undefined,
  key: keyof PeriodChecklistCounts,
  formatValue?: (value: number) => string,
): string => {
  if (!counts) return '—';
  const value = counts[key];
  if (value === null || value === undefined) return '—';
  return formatValue ? formatValue(value) : String(value);
};

const requiredItems: Array<ChecklistItemDef> = [
  {
    key: 'bankTransactionsReconciliation',
    label: 'Confirm bank transactions awaiting reconciliation',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <AccountBalanceOutlined fontSize="small" />,
  },
  {
    key: 'bankBalancesStatement',
    label: 'Check end of quarter bank balances against statement balances',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('account', Number(count))}`,
    icon: <AccountBalanceOutlined fontSize="small" />,
  },
  {
    key: 'incomeExpensesInvestmentsAndRealisations',
    label:
      'Confirm all incomes, expenses, investments and realisations entered',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('action', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'transactionRates',
    label: 'Approve outstanding transaction rates',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'accrualsPayablesReceivables',
    label: 'Approve all outstanding accruals, payables and receivables',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <SyncAltOutlined fontSize="small" />,
  },
  {
    key: 'prepaymentResolutions',
    label: 'Approve prepayment resolutions',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <KeyboardDoubleArrowRightOutlined fontSize="small" />,
  },
  {
    key: 'companyValuations',
    label: 'Confirm company valuations',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('company', Number(count), 'companies')}`,
    icon: <ApartmentOutlined fontSize="small" />,
  },
  {
    key: 'unrealisedFxGainsAndLosses',
    label: 'Post unrealised FX gains and losses',
    subLabelTemplate: (count) =>
      `Review ${count} ${pluralize('transaction', Number(count))}`,
    icon: <ShowChartOutlined fontSize="small" />,
  },
];

const optionalItems: Array<ChecklistItemDef> = [
  {
    key: 'awaitingCommitments',
    label: 'Close awaiting commitments',
    subLabelTemplate: (count) =>
      `${count} ${pluralize('commitment', Number(count))} pending close`,
    icon: <CheckCircleOutlined fontSize="small" />,
  },
  {
    key: 'transactionsPendingCall',
    label: 'Transactions pending call',
    subLabelTemplate: (amount) => `${amount} uncalled capital`,
    formatValue: (value) => numberToCurrencyString(value),
    icon: <LocalAtmOutlined fontSize="small" />,
  },
];

const PeriodEndReviewDetail: React.FC = () => {
  const { periodId } = useParams<RouteParams>();
  const { vehicle, setBreadcrumbs, breadcrumbs } = useLayoutContext();
  const history = useHistory();
  const handleMutation = useHandleTrpcMutation<string>();
  const { success, error } = useNotification();

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);

  const [period] = api.accountingPeriod.getPeriodById.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
    id: periodId,
  });

  const { data: checklistCounts } =
    api.accountingPeriod.getPeriodChecklistCounts.useQuery(
      { vehicleId: vehicle?._id ?? '', id: periodId },
      { enabled: !!vehicle?._id && !!periodId },
    );

  const [optimisticChecklist, setOptimisticChecklist] = useState<
    Partial<Record<keyof AccountingPeriodChecklist, boolean>>
  >({});

  const [pendingItems, setPendingItems] = useState<
    Set<keyof AccountingPeriodChecklist>
  >(new Set());

  const updateChecklistItem =
    api.accountingPeriod.updateChecklistItem.useMutation(
      handleMutation({
        refetchType: 'active',
      }),
    );

  const uncheckAllChecklistItems =
    api.accountingPeriod.uncheckAllChecklistItems.useMutation(
      handleMutation({
        refetchType: 'active',
      }),
    );

  const approvePeriodMutation = api.accountingPeriod.approvePeriod.useMutation(
    handleMutation({
      refetchType: 'active',
    }),
  );

  const handleApprove = () => {
    if (!vehicle?._id || !periodId) return;
    setIsProcessingApproval(true);
    approvePeriodMutation.mutate(
      { vehicleId: vehicle._id, id: periodId, approve: true },
      {
        onSuccess: () => success('Period end review approved successfully.'),
        onSettled: () => setIsProcessingApproval(false),
      },
    );
  };

  const handleUnlockConfirm = () => {
    if (!vehicle?._id || !periodId) return;
    approvePeriodMutation.mutate(
      { vehicleId: vehicle._id, id: periodId, approve: false },
      { onSuccess: () => setUnlockModalOpen(false) },
    );
  };

  const getChecked = (key: keyof AccountingPeriodChecklist): boolean => {
    if (key in optimisticChecklist) return optimisticChecklist[key] ?? false;
    return period.checklist[key].confirmed;
  };

  const getChecklistItem = (
    key: keyof AccountingPeriodChecklist,
  ): ChecklistItem | undefined => period.checklist[key];

  const allChecklistKeys = [
    ...requiredItems.map((i) => i.key),
    ...optionalItems.map((i) => i.key),
  ];

  const handleToggle = (
    item: keyof AccountingPeriodChecklist,
    confirmed: boolean,
  ) => {
    if (!vehicle?._id || !periodId) return;

    if (item === 'bankTransactionsReconciliation' && !confirmed) {
      if (pendingItems.size > 0) return;
      setOptimisticChecklist(
        fromPairs(allChecklistKeys.map((key) => [key, false])),
      );
      uncheckAllChecklistItems.mutate(
        { vehicleId: vehicle._id, id: periodId },
        {
          onSettled: (_, err) => {
            if (err) setOptimisticChecklist({});
          },
        },
      );
      return;
    }

    setPendingItems((prev) => new Set(prev).add(item));
    setOptimisticChecklist((prev) => ({ ...prev, [item]: confirmed }));
    updateChecklistItem.mutate(
      { vehicleId: vehicle._id, id: periodId, item, confirmed },
      {
        onSettled: (_, err) => {
          setPendingItems((prev) => {
            const next = new Set(prev);
            next.delete(item);
            return next;
          });
          if (err) {
            setOptimisticChecklist((prev) => {
              const { [item]: _removed, ...rest } = prev;
              return rest;
            });
          }
        },
        onError: (err) => {
          captureException(err);
          error('Failed to update checklist item.');
        },
      },
    );
  };

  const isFirstItemConfirmed = getChecked('bankTransactionsReconciliation');

  const confirmedRequiredCount = requiredItems.filter((i) =>
    getChecked(i.key),
  ).length;

  const allRequiredConfirmed = confirmedRequiredCount === requiredItems.length;

  const completionPercentage = Math.round(
    (confirmedRequiredCount / requiredItems.length) * 100,
  );

  const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;

  useEffect(() => {
    if (breadcrumbs[breadcrumbs.length - 1] === dateRange) return;
    setBreadcrumbs([
      { label: 'Period End Review', link: LP_PERIOD_END_REVIEW },
      dateRange,
    ]);
  }, [dateRange, breadcrumbs]);

  const isApproved = period.status === AccountingPeriodStatus.Approved;

  if (isProcessingApproval) {
    return (
      <Container>
        <Column spacing="lg">
          <Row centered spacing="sm">
            <IconButtonSquare
              variant="square"
              onClick={() => history.push(LP_PERIOD_END_REVIEW)}
            >
              <ArrowBack fontSize="small" />
            </IconButtonSquare>
            <Typography variant="h4">{dateRange}</Typography>
          </Row>
          <Divider />
        </Column>
        <ProcessingPage
          title="Processing current period end"
          subtitle="This will only take a moment."
        />
      </Container>
    );
  }

  return (
    <Container>
      <Column spacing="lg">
        <Row centered spacing="sm">
          <IconButtonSquare
            variant="square"
            onClick={() => history.push(LP_PERIOD_END_REVIEW)}
          >
            <ArrowBack fontSize="small" />
          </IconButtonSquare>
          <Typography variant="h4">{dateRange}</Typography>
        </Row>
        <Divider />
        <Row centered justify="space-between">
          <Typography variant="body2" color="text.secondary">
            Required for period end processing
          </Typography>
          {isApproved ? (
            <Typography variant="body2" color="text.secondary">
              Approved on {period?.approvedAt?.toReadable()}
            </Typography>
          ) : (
            <Row centered spacing="xs">
              <Typography variant="body2" color="text.secondary">
                {completionPercentage}%
              </Typography>
              <CircularProgress
                size={24}
                variant="determinate"
                value={completionPercentage}
              />
            </Row>
          )}
        </Row>
        <Column spacing="sm">
          {requiredItems.map((item, index) => {
            const checklistItem = getChecklistItem(item.key);
            const isChecked = getChecked(item.key);
            return (
              <ChecklistCard
                key={item.key}
                icon={<ChecklistItemIcon>{item.icon}</ChecklistItemIcon>}
                primaryLabel={
                  <Typography variant="h6">{item.label}</Typography>
                }
                secondaryLabel={
                  isChecked &&
                  checklistItem?.confirmedByLabel &&
                  checklistItem?.confirmedAt ? (
                    <Typography variant="body2" color="text.secondary">
                      Completed by {checklistItem.confirmedByLabel} on{' '}
                      {checklistItem.confirmedAt.toReadable()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {item.subLabelTemplate(
                        formatCount(
                          checklistCounts,
                          item.key,
                          item.formatValue,
                        ),
                      )}
                    </Typography>
                  )
                }
                actions={
                  <AppCheckbox
                    checked={isChecked}
                    disabled={
                      isApproved ||
                      pendingItems.has(item.key) ||
                      uncheckAllChecklistItems.isPending ||
                      (index > 0 && !isFirstItemConfirmed)
                    }
                    onChange={(e) => handleToggle(item.key, e.target.checked)}
                  />
                }
                secondaryActions={
                  <IconButton
                    size="small"
                    color="default"
                    onClick={
                      item.key === 'accrualsPayablesReceivables'
                        ? () =>
                            history.push(
                              createReviewAccrualsRoute(
                                period.periodStart,
                                period.periodEnd,
                                periodId,
                              ),
                            )
                        : item.key === 'unrealisedFxGainsAndLosses'
                        ? () =>
                            history.push(
                              createLpPostUnrealisedFXGainsRoute(
                                period.periodStart,
                                period.periodEnd,
                                periodId,
                              ),
                            )
                        : item.key === 'transactionRates'
                        ? () =>
                            history.push(
                              createApproveTransactionRatesRoute(
                                period.periodStart,
                                period.periodEnd,
                                periodId,
                              ),
                            )
                        : item.key === 'prepaymentResolutions'
                        ? () =>
                            history.push(
                              createApprovePrepayments(
                                period.periodStart,
                                period.periodEnd,
                                periodId,
                              ),
                            )
                        : undefined
                    }
                  >
                    <ArrowForward fontSize="small" />
                  </IconButton>
                }
              />
            );
          })}
        </Column>
        <Divider />
        <Typography variant="body2" color="text.secondary">
          Optional reviews
        </Typography>
        <Grid container spacing={2}>
          {optionalItems.map((item) => {
            const checklistItem = getChecklistItem(item.key);
            const isChecked = getChecked(item.key);
            return (
              <Grid key={item.key} size={{ xs: 12, sm: 6 }}>
                <ChecklistCard
                  icon={<ChecklistItemIcon>{item.icon}</ChecklistItemIcon>}
                  primaryLabel={
                    <Typography variant="h6">{item.label}</Typography>
                  }
                  secondaryLabel={
                    isChecked &&
                    checklistItem?.confirmedByLabel &&
                    checklistItem?.confirmedAt ? (
                      <Typography variant="body2" color="text.secondary">
                        Completed by {checklistItem.confirmedByLabel} on{' '}
                        {checklistItem.confirmedAt.toReadable()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {item.subLabelTemplate(
                          formatCount(
                            checklistCounts,
                            item.key,
                            item.formatValue,
                          ),
                        )}
                      </Typography>
                    )
                  }
                  actions={
                    <AppCheckbox
                      checked={isChecked}
                      disabled={
                        isApproved ||
                        pendingItems.has(item.key) ||
                        uncheckAllChecklistItems.isPending
                      }
                      onChange={(e) => handleToggle(item.key, e.target.checked)}
                    />
                  }
                  secondaryActions={
                    <IconButton size="small" color="default">
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </Column>
      <BottomBar>
        {isApproved ? (
          <Tooltip
            title={
              !period.isLatestApprovedPeriod
                ? 'Only the most recent accounting period can be re-opened. Please re-open later periods first.'
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                size="medium"
                disabled={
                  approvePeriodMutation.isPending ||
                  !period.isLatestApprovedPeriod
                }
                onClick={() => setUnlockModalOpen(true)}
              >
                Unlock period
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="By approving, you will lock this period for review purposes and accounting statements will be automatically generated.">
            <span>
              <Button
                variant="contained"
                size="medium"
                endIcon={<InfoOutlined fontSize="small" />}
                disabled={
                  !allRequiredConfirmed || approvePeriodMutation.isPending
                }
                onClick={handleApprove}
              >
                Approve current period end
              </Button>
            </span>
          </Tooltip>
        )}
      </BottomBar>
      <ConfirmDialog
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        title="Unlock period"
        content={
          <Typography variant="body2">
            Unlocking a period end and making changes will result in changes to
            accounting statements. If these have been sent to investors, they
            will need to be reissued.
          </Typography>
        }
        loading={approvePeriodMutation.isPending}
        actions={{
          cancel: { label: 'Cancel' },
          confirm: { onClick: handleUnlockConfirm, label: 'Unlock period' },
        }}
      />
    </Container>
  );
};

export default PeriodEndReviewDetail;
